import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription
} from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { RadioGroup, RadioGroupItem } from '@ui/radio-group.tsx';
import { Calendar } from '@ui/calendar.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@ui/popover.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ui/select.tsx';
import { Button } from '@ui/button.tsx';
import { Card } from '@ui/card.tsx';
import { cn } from '@r/utils/utils.ts';
import { CalendarIcon } from 'lucide-react';
import { format, toDate, startOfDay, addDays } from 'date-fns';
import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import { LOGIC_TRANSLATION_KEYS } from '@admin/types/workspace/logic.ts';
import type { RenewalProgramFormSchema } from '@admin/components/renew-program/renewal-program-form-schema.ts';
import type { LogicWorkspaceWorkspaceReadModel } from '@r/api/generated/client.schemas.ts';
import { LogicLogicReadModelType } from '@r/api/generated/client.schemas.ts';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ui/table.tsx';

interface RenewalProgramLogicStepContentProps {
  form: UseFormReturn<RenewalProgramFormSchema>;
  currentLogic: LogicWorkspaceWorkspaceReadModel;
}

/** Étape 1 du renouvellement : dates, expiration des points, objectifs (si logique cible). */
export function RenewalProgramLogicStepContent({
  form,
  currentLogic
}: RenewalProgramLogicStepContentProps) {
  const { t } = useTranslation();

  const startAt = form.watch('startAt');
  const endAt = form.watch('endAt');
  const pointExpirationType = form.watch('pointExpirationType');

  const startOfToday = startOfDay(new Date());
  const currentLogicEndAt = currentLogic?.endAt ? startOfDay(toDate(currentLogic.endAt)) : null;
  // La nouvelle logique doit commencer le lendemain de la fin de l'actuelle (backend : pas de chevauchement le jour de fin).
  const firstAllowedStart = currentLogicEndAt ? addDays(currentLogicEndAt, 1) : startOfToday;
  const minStartAt =
    currentLogicEndAt && firstAllowedStart.getTime() > startOfToday.getTime()
      ? firstAllowedStart
      : startOfToday;

  const logicType = currentLogic?.logic?.type;
  const conversionMethodLabel =
    logicType && logicType in LOGIC_TRANSLATION_KEYS
      ? t(LOGIC_TRANSLATION_KEYS[logicType as keyof typeof LOGIC_TRANSLATION_KEYS])
      : (currentLogic?.logic?.name ?? '—');

  const isTargetLogicType =
    logicType === LogicLogicReadModelType.TARGET_PERF ||
    logicType === LogicLogicReadModelType.TARGET_POINT;
  const targets = currentLogic?.targets ?? [];
  const targetTypeLabel =
    logicType && logicType in LOGIC_TRANSLATION_KEYS
      ? t(LOGIC_TRANSLATION_KEYS[logicType as keyof typeof LOGIC_TRANSLATION_KEYS])
      : '—';

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">{t('form.workspace.renewal.logic.title')}</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('form.workspace.renewal.logic.description1')}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('form.workspace.renewal.logic.description2')}
        </p>
      </div>

      <form className="space-y-6">
        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            <FormLabel className="text-base">{t('form.workspace.convert.method.label')}</FormLabel>
          </div>
          <Input
            value={conversionMethodLabel}
            readOnly
            disabled
            className="bg-muted text-muted-foreground"
          />
        </div>

        {/* Dates d'opération */}
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="startAt"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{t('form.workspace.operation.date.start')}</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          'w-full pl-3 text-left font-normal',
                          !field.value && 'text-muted-foreground'
                        )}
                      >
                        {field.value ? (
                          format(field.value, 'dd/MM/yyyy')
                        ) : (
                          <span>{t('common.date.placeholder')}</span>
                        )}
                        <CalendarIcon className="ml-auto w-4 h-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-auto" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      defaultMonth={field.value ?? minStartAt}
                      disabled={(date) => {
                        const d = startOfDay(date);
                        return (
                          d.getTime() < minStartAt.getTime() ||
                          (endAt && d.getTime() >= startOfDay(endAt).getTime())
                        );
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="endAt"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{t('form.workspace.operation.date.end')}</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          'w-full pl-3 text-left font-normal',
                          !field.value && 'text-muted-foreground'
                        )}
                      >
                        {field.value ? (
                          format(field.value, 'dd/MM/yyyy')
                        ) : (
                          <span>{t('common.date.placeholder')}</span>
                        )}
                        <CalendarIcon className="ml-auto w-4 h-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-auto" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      defaultMonth={field.value ?? startAt}
                      disabled={(date) => {
                        if (!startAt) return true;
                        const d = startOfDay(date);
                        return d.getTime() < startOfDay(startAt).getTime();
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Liste des références objectifs (objectif euros / objectif point) */}
        {isTargetLogicType && targets.length > 0 && (
          <div className="space-y-2">
            <FormLabel className="text-base">
              {t('form.workspace.renewal.targets.list.label')}
            </FormLabel>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('form.target.reference.label')}</TableHead>
                    <TableHead>{t('form.target.type.label')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {targets.map((target, index) => (
                    <TableRow key={target.reference ?? index}>
                      <TableCell className="font-medium">{target.reference}</TableCell>
                      <TableCell>{targetTypeLabel}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* Date d'expiration des points */}
        <div className="space-y-3 w-full">
          <h3 className="text-base font-semibold">{t('form.workspace.point.expiration.title')}</h3>
          <FormDescription>{t('form.workspace.point.expiration.description')}</FormDescription>
          <FormField
            control={form.control}
            name="pointExpirationType"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <div className="grid gap-4 py-4">
                  <RadioGroup
                    onValueChange={field.onChange}
                    value={field.value}
                    className="grid-flow-col"
                  >
                    <FormItem className="flex items-center space-x-3 space-y-0">
                      <FormControl>
                        <RadioGroupItem value="fixed" />
                      </FormControl>
                      <FormLabel className="font-normal">
                        {t('form.workspace.point.expiration.option.fixed')}
                      </FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center space-x-3 space-y-0">
                      <FormControl>
                        <RadioGroupItem value="yearEnd" />
                      </FormControl>
                      <FormLabel className="font-normal">
                        {t('form.workspace.point.expiration.option.year.end')}
                      </FormLabel>
                    </FormItem>
                  </RadioGroup>
                </div>
              </FormItem>
            )}
          />
          {pointExpirationType === 'fixed' && (
            <FormField
              control={form.control}
              name="pointExpirationDate"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t('form.workspace.point.expiration.date.label')}</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-[240px] pl-3 text-left font-normal',
                            !field.value && 'text-muted-foreground'
                          )}
                        >
                          {field.value ? (
                            format(field.value, 'PPP')
                          ) : (
                            <span>{t('form.workspace.point.expiration.date.placeholder')}</span>
                          )}
                          <CalendarIcon className="ml-auto w-4 h-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 w-auto" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        defaultMonth={
                          field.value ?? (startAt ? startOfDay(startAt) : startOfDay(new Date()))
                        }
                        disabled={(date) => {
                          const minDay = startAt ? startOfDay(startAt) : startOfDay(new Date());
                          const d = startOfDay(date);
                          return d.getTime() < minDay.getTime();
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          {pointExpirationType === 'yearEnd' && (
            <FormField
              control={form.control}
              name="pointExpirationYearOffset"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t('form.workspace.point.expiration.date.label')}</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(Number(value))}
                    value={field.value !== undefined ? String(field.value) : ''}
                  >
                    <FormControl>
                      <SelectTrigger className="w-[240px]">
                        <SelectValue
                          placeholder={t('form.workspace.point.expiration.select.placeholder')}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Array.from({ length: 11 }, (_, index) => index).map((offset) => (
                        <SelectItem key={offset} value={String(offset)}>
                          {offset === 0
                            ? t('form.workspace.point.expiration.select.option.n')
                            : t('form.workspace.point.expiration.select.option.n.plus', {
                                offset
                              })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          <Card className="p-4 space-y-1 bg-purple-200">
            <p className="font-bold text-purple-600">
              {t('form.workspace.point.expiration.notice.title')}
            </p>
            <p className="italic text-purple-600">
              {t('form.workspace.point.expiration.notice.example.line1')}
              <br />
              {t('form.workspace.point.expiration.notice.example.line2')}
            </p>
          </Card>
        </div>
      </form>
    </div>
  );
}
